import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Plane, Ship, Truck, Globe, FileCheck, Handshake,
  CheckCircle, ArrowRight, Package
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services } from '../data/mock';

const iconMap = {
  Plane: Plane,
  Ship: Ship,
  Truck: Truck,
  Globe: Globe,
  FileCheck: FileCheck,
  Handshake: Handshake
};

const ServicesPage = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-32 bg-[#1B4F5D]">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <span className="text-[#408D4D] font-semibold tracking-wider uppercase text-sm">What We Offer</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-6">
              Comprehensive Logistics Services
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              From freight forwarding to customs clearance, we provide end-to-end logistics 
              solutions tailored to your business needs.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon] || Package;
              return (
                <Card 
                  key={service.id} 
                  className="bg-white border-0 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden group"
                >
                  <CardContent className="p-0">
                    <div className="flex flex-col md:flex-row">
                      <div className="md:w-1/3 bg-[#1B4F5D] p-8 flex items-center justify-center group-hover:bg-[#408D4D] transition-colors duration-300">
                        <Icon className="text-white" size={64} />
                      </div>
                      <div className="md:w-2/3 p-8">
                        <h3 className="text-2xl font-bold text-[#1B4F5D] mb-4">{service.title}</h3>
                        <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                        <div className="grid grid-cols-2 gap-3">
                          {service.features.map((feature, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm">
                              <CheckCircle size={16} className="text-[#408D4D] flex-shrink-0" />
                              <span className="text-gray-600">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Process */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#408D4D] font-semibold tracking-wider uppercase text-sm">How It Works</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1B4F5D] mt-3 mb-4">
              Our Process
            </h2>
            <p className="text-gray-600 text-lg">
              Simple, streamlined, and efficient—here's how we handle your shipments
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Request Quote', desc: 'Contact us with your shipment details for a free quote' },
              { step: '02', title: 'Documentation', desc: 'We handle all paperwork and customs requirements' },
              { step: '03', title: 'Shipping', desc: 'Your goods are transported safely and efficiently' },
              { step: '04', title: 'Delivery', desc: 'Receive your shipment on time, every time' }
            ].map((item, index) => (
              <div key={index} className="relative text-center">
                <div className="w-20 h-20 mx-auto rounded-full bg-[#408D4D]/10 flex items-center justify-center mb-6 relative">
                  <span className="text-2xl font-bold text-[#408D4D]">{item.step}</span>
                  {index < 3 && (
                    <div className="hidden md:block absolute left-full top-1/2 w-full h-0.5 bg-[#408D4D]/20" />
                  )}
                </div>
                <h3 className="text-xl font-bold text-[#1B4F5D] mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#1B4F5D]">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Contact us today for a free consultation and quote
            </p>
            <Link to="/contact">
              <Button className="bg-[#408D4D] hover:bg-[#2D6636] text-white px-10 py-6 text-lg rounded-lg font-semibold">
                Request a Quote
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
